/**
 * API 응답 봉투(envelope). apps/api 의 모든 라우트는 이 형태로만 응답한다.
 * 예외를 던져 500 을 흘리지 말고 `err()` 로 변환할 것.
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiError };

export interface ApiError {
  /** 클라이언트가 분기에 쓰는 안정적인 코드. 문구가 아니라 이 값으로 분기한다. */
  code: ApiErrorCode;
  /** 사용자에게 그대로 보여줄 수 있는 한국어 메시지. */
  message: string;
  /** 디버깅용 부가 정보. 인증정보·토큰은 절대 넣지 않는다. */
  details?: unknown;
}

export type ApiErrorCode =
  | "UNAUTHENTICATED"
  /** 저장된 Figma PAT가 만료·회수되었거나 필요한 읽기 범위가 없다. */
  | "FIGMA_TOKEN_INVALID"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "VALIDATION_FAILED"
  | "DESIGN_SYSTEM_NOT_FOUND"
  | "AI_UPSTREAM_ERROR"
  | "AI_TIMEOUT"
  | "AI_INVALID_RESPONSE"
  | "DEPLOY_FAILED"
  | "RATE_LIMITED"
  /** 다른 곳에서 먼저 저장되어 낙관적 잠금에 걸렸다. */
  | "CONFLICT"
  | "INTERNAL";

export const ok = <T>(data: T): ApiResult<T> => ({ ok: true, data });

export const err = (
  code: ApiErrorCode,
  message: string,
  details?: unknown,
): ApiResult<never> => ({ ok: false, error: { code, message, details } });

/**
 * ApiErrorCode → HTTP 상태 코드. api 레이어에서만 사용한다.
 *
 * `as const satisfies` 로 값을 리터럴 타입으로 유지한다. `Record<…, number>` 로
 * 선언하면 `number` 로 넓어져 Hono 의 `c.json(body, status)` 시그니처와 맞지 않는다.
 */
export const HTTP_STATUS = {
  UNAUTHENTICATED: 401,
  FIGMA_TOKEN_INVALID: 400,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_FAILED: 400,
  DESIGN_SYSTEM_NOT_FOUND: 404,
  AI_UPSTREAM_ERROR: 502,
  AI_TIMEOUT: 504,
  AI_INVALID_RESPONSE: 502,
  DEPLOY_FAILED: 502,
  RATE_LIMITED: 429,
  CONFLICT: 409,
  INTERNAL: 500,
} as const satisfies Record<ApiErrorCode, number>;
